import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 烹饪美食 - 横屏「食谱卡片」风格
 *
 * 核心亮点：
 * - 食材卡片弹入
 * - 步骤进度条 + 编号动画
 * - 营养成分环形图
 * - 风味雷达图
 * - 厨具展示动画
 * - 搭配推荐卡片
 * - 计时器动画
 * - 浮动装饰粒子
 * - 暖色调美食风格
 *
 * 尺寸：1920x1080 横屏
 */

interface Ingredient {
  name: string;
  amount: string;
  icon: string;
}

interface CookStep {
  text: string;
  duration: string;
  icon: string;
}

interface Nutrient {
  name: string;
  value: number;
  max: number;
  color: string;
  unit: string;
}

interface FlavorDimension {
  name: string;
  value: number;
  color: string;
}

interface Equipment {
  name: string;
  icon: string;
}

interface CookingRecipeTemplateProps {
  dishName: string;
  subtitle: string;
  difficulty: string;
  totalTime: string;
  servings: string;
  category: string;
  rating: number;
  ingredients: Ingredient[];
  steps: CookStep[];
  nutrients: Nutrient[];
  flavorProfile: FlavorDimension[];
  equipment: Equipment[];
  pairingSuggestion: string;
  storageInfo: string;
  chefTip: string;
}

const DEFAULT_PROPS: CookingRecipeTemplateProps = {
  dishName: '日式照烧鸡腿饭',
  subtitle: 'Teriyaki Chicken Don',
  difficulty: '⭐⭐ 简单',
  totalTime: '30 分钟',
  servings: '2 人份',
  category: '日式料理',
  rating: 4.8,
  ingredients: [
    { name: '鸡腿', amount: '2 只', icon: '🍗' },
    { name: '酱油', amount: '3 勺', icon: '🫘' },
    { name: '味醂', amount: '2 勺', icon: '🍶' },
    { name: '白糖', amount: '1 勺', icon: '🧂' },
    { name: '米饭', amount: '2 碗', icon: '🍚' },
    { name: '西兰花', amount: '适量', icon: '🥦' },
  ],
  steps: [
    { text: '鸡腿去骨，用叉子扎孔便于入味', duration: '5min', icon: '🔪' },
    { text: '酱油 + 味醂 + 白糖调成照烧汁', duration: '2min', icon: '🥄' },
    { text: '平底锅煎鸡腿至两面金黄', duration: '8min', icon: '🍳' },
    { text: '倒入照烧汁，小火收汁至浓稠', duration: '5min', icon: '🔥' },
    { text: '切片摆盘，配上米饭和西兰花', duration: '3min', icon: '🍽️' },
  ],
  nutrients: [
    { name: '热量', value: 580, max: 800, color: '#ef4444', unit: 'kcal' },
    { name: '蛋白质', value: 35, max: 50, color: '#3b82f6', unit: 'g' },
    { name: '碳水', value: 65, max: 100, color: '#f59e0b', unit: 'g' },
    { name: '脂肪', value: 18, max: 40, color: '#22c55e', unit: 'g' },
  ],
  flavorProfile: [
    { name: '甜', value: 70, color: '#f472b6' },
    { name: '咸', value: 85, color: '#60a5fa' },
    { name: '鲜', value: 95, color: '#fbbf24' },
    { name: '辣', value: 10, color: '#ef4444' },
    { name: '酸', value: 15, color: '#34d399' },
  ],
  equipment: [
    { name: '平底锅', icon: '🍳' },
    { name: '菜刀', icon: '🔪' },
    { name: '砧板', icon: '🪵' },
    { name: '量勺', icon: '🥄' },
    { name: '碗碟', icon: '🍽️' },
  ],
  pairingSuggestion: '搭配一杯冰镇乌龙茶或日式味噌汤，口感更佳',
  storageInfo: '密封冷藏可保存 2 天，食用前微波加热 2 分钟',
  chefTip: '煎鸡腿时先皮面朝下，中火慢煎逼出油脂，皮会更脆',
};

const FLOATING_PARTICLES = [
  { emoji: '🌶️', x: 5, y: 8, speed: 0.6, size: 42, delay: 0 },
  { emoji: '🧄', x: 92, y: 15, speed: 0.4, size: 38, delay: 20 },
  { emoji: '🍋', x: 15, y: 85, speed: 0.5, size: 40, delay: 40 },
  { emoji: '🌿', x: 88, y: 78, speed: 0.7, size: 36, delay: 10 },
  { emoji: '🧅', x: 50, y: 5, speed: 0.3, size: 38, delay: 35 },
  { emoji: '✨', x: 75, y: 90, speed: 0.8, size: 34, delay: 15 },
  { emoji: '🍃', x: 30, y: 92, speed: 0.45, size: 36, delay: 50 },
  { emoji: '💨', x: 60, y: 10, speed: 0.55, size: 34, delay: 25 },
];

export const CookingRecipeTemplate: React.FC<Partial<CookingRecipeTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  // Ingredients
  const ingStart = 25;
  const ingInterval = 12;

  // Equipment
  const equipStart = ingStart + p.ingredients.length * ingInterval + 15;
  const equipInterval = 10;

  // Steps
  const stepStart = equipStart + p.equipment.length * equipInterval + 15;
  const stepInterval = 40;
  const activeStep = Math.min(
    Math.floor(interpolate(frame, [stepStart, stepStart + p.steps.length * stepInterval], [0, p.steps.length], { extrapolateRight: 'clamp' })),
    p.steps.length - 1
  );

  // Flavor profile
  const flavorStart = stepStart + p.steps.length * stepInterval + 15;
  const flavorInterval = 12;

  // Nutrients
  const nutStart = flavorStart + p.flavorProfile.length * flavorInterval + 15;

  // Pairing & storage
  const pairingStart = nutStart + 70;
  const pairingOpacity = interpolate(frame, [pairingStart, pairingStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Chef tip
  const tipStart = pairingStart + 30;
  const tipOpacity = interpolate(frame, [tipStart, tipStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Timer animation (active step countdown)
  const timerAngle = interpolate(frame, [stepStart, stepStart + p.steps.length * stepInterval], [0, 360], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #fceee0 0%, #f5d5b8 50%, #f0c89a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: '#1c1917',
        overflow: 'hidden',
      }}
    >
      {/* ====== Floating background particles ====== */}
      {FLOATING_PARTICLES.map((particle, i) => {
        const delayedFrame = Math.max(0, frame - particle.delay);
        const yOffset = Math.sin(delayedFrame * 0.03 * particle.speed) * 15;
        const xOffset = Math.cos(delayedFrame * 0.02 * particle.speed) * 8;
        const rotation = delayedFrame * particle.speed * 0.8;
        const opacity = interpolate(frame, [particle.delay, particle.delay + 30], [0, 0.55], { extrapolateRight: 'clamp' });
        return (
          <div
            key={`particle-${i}`}
            style={{
              position: 'absolute',
              left: `${particle.x + xOffset * 0.1}%`,
              top: `${particle.y + yOffset * 0.1}%`,
              fontSize: particle.size,
              opacity,
              transform: `rotate(${rotation}deg)`,
              pointerEvents: 'none',
            }}
          >
            {particle.emoji}
          </div>
        );
      })}

      {/* ====== Decorative steam wisps ====== */}
      {[0, 1, 2].map((i) => {
        const steamDelay = i * 25;
        const steamFrame = Math.max(0, frame - steamDelay);
        const steamY = interpolate(steamFrame, [0, 120], [0, -40], { extrapolateRight: 'extend' }) % 60;
        const steamOpacity = interpolate(steamFrame % 120, [0, 40, 80, 120], [0, 0.15, 0.1, 0], { extrapolateRight: 'clamp' });
        return (
          <div
            key={`steam-${i}`}
            style={{
              position: 'absolute',
              right: 120 + i * 80,
              top: 60 + steamY,
              fontSize: 32,
              opacity: steamOpacity,
              filter: 'blur(1px)',
              pointerEvents: 'none',
            }}
          >
            ♨️
          </div>
        );
      })}

      {/* ====== Header ====== */}
      <div
        style={{
          position: 'absolute',
          top: 30,
          left: 60,
          right: 60,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          opacity: headerProgress,
          transform: `translateY(${(1 - headerProgress) * -12}px)`,
        }}
      >
        <div style={{ fontSize: 44 }}>🍳</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <h1 style={{ fontSize: 38, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>{p.dishName}</h1>
            {/* Category badge */}
            <div style={{
              padding: '3px 12px',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              borderRadius: 16,
              fontSize: 22,
              color: 'white',
              fontWeight: 700,
              letterSpacing: '0.05em',
            }}>
              {p.category}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 2 }}>
            <p style={{ fontSize: 22, color: '#92400e', margin: 0, fontStyle: 'italic' }}>{p.subtitle}</p>
            {/* Star rating */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 18, color: '#f59e0b' }}>★</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: '#92400e' }}>{p.rating}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {[
            { label: p.difficulty },
            { label: `⏱ ${p.totalTime}` },
            { label: `👥 ${p.servings}` },
          ].map((badge, i) => (
            <div key={i} style={{ padding: '5px 14px', background: 'rgba(146,64,14,0.06)', borderRadius: 20, fontSize: 24, color: '#92400e', fontWeight: 600 }}>
              {badge.label}
            </div>
          ))}
        </div>
      </div>

      {/* ====== Three-column body ====== */}
      <div style={{ position: 'absolute', top: 150, left: 60, right: 60, bottom: 46, display: 'flex', gap: 30 }}>

        {/* ===== Left column: ingredients + equipment + flavor ===== */}
        <div style={{ width: 870, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16}}>
          {/* Ingredients */}
          <div>
            <div style={{ fontSize: 26, color: '#92400e', letterSpacing: '0.1em', marginBottom: 8, fontWeight: 700 }}>📋 食材清单</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {p.ingredients.map((ing, i) => {
                const start = ingStart + i * ingInterval;
                const scale = spring({ frame: frame - start, fps, from: 0, to: 1, config: { damping: 10, stiffness: 150, mass: 0.5 } });
                return (
                  <div
                    key={i}
                    style={{
                      padding: '7px 12px',
                      background: 'white',
                      borderRadius: 10,
                      border: '1px solid rgba(0,0,0,0.04)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      opacity: scale,
                      transform: `scale(${0.85 + scale * 0.15})`,
                    }}
                  >
                    <span style={{ fontSize: 28 }}>{ing.icon}</span>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 700 }}>{ing.name}</div>
                      <div style={{ fontSize: 22, color: '#92400e' }}>{ing.amount}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Equipment */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 26, color: '#92400e', letterSpacing: '0.1em', marginBottom: 8, fontWeight: 700 }}>🔧 所需厨具</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {p.equipment.map((eq, i) => {
                const start = equipStart + i * equipInterval;
                const pop = spring({ frame: frame - start, fps, from: 0, to: 1, config: { damping: 12, stiffness: 180, mass: 0.4 } });
                return (
                  <div
                    key={i}
                    style={{
                      padding: '10px 14px',
                      background: 'rgba(146,64,14,0.05)',
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      opacity: pop,
                      transform: `scale(${0.8 + pop * 0.2}) translateY(${(1 - pop) * 8}px)`,
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{eq.icon}</span>
                    <span style={{ fontSize: 24, fontWeight: 600, color: '#78350f' }}>{eq.name}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Flavor Profile */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 26, color: '#92400e', letterSpacing: '0.1em', marginBottom: 8, fontWeight: 700 }}>🎨 风味档案</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {p.flavorProfile.map((flavor, i) => {
                const start = flavorStart + i * flavorInterval;
                const barProgress = interpolate(frame, [start, start + 30], [0, flavor.value / 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
                const opacity = interpolate(frame, [start, start + 12], [0, 1], { extrapolateRight: 'clamp' });
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity }}>
                    <div style={{ width: 36, fontSize: 22, fontWeight: 700, color: '#78350f', textAlign: 'right' }}>{flavor.name}</div>
                    <div style={{ flex: 1, height: 16, background: 'rgba(0,0,0,0.04)', borderRadius: 7, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${barProgress * 100}%`,
                          height: '100%',
                          background: `linear-gradient(90deg, ${flavor.color}88, ${flavor.color})`,
                          borderRadius: 7,
                        }}
                      />
                    </div>
                    <div style={{ width: 36, fontSize: 22, fontWeight: 600, color: flavor.color, textAlign: 'left' }}>{flavor.value}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Nutrients */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 26, color: '#92400e', letterSpacing: '0.1em', marginBottom: 8, fontWeight: 700 }}>🥗 营养成分（每份）</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 }}>
              {p.nutrients.map((nut, i) => {
                const start = nutStart + i * 14;
                const progress = interpolate(frame, [start, start + 30], [0, nut.value / nut.max], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
                const opacity = interpolate(frame, [start, start + 12], [0, 1], { extrapolateRight: 'clamp' });
                const r = 34;
                const circumference = 2 * Math.PI * r;

                return (
                  <div key={i} style={{ textAlign: 'center', opacity }}>
                    <svg width="80" height="80" viewBox="0 0 80 80" style={{ margin: '0 auto' }}>
                      <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(0,0,0,0.04)" strokeWidth="5" />
                      <circle
                        cx="40" cy="40" r={r} fill="none" stroke={nut.color} strokeWidth="5" strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={circumference * (1 - progress)}
                        transform="rotate(-90, 40, 40)"
                      />
                      <text x="40" y="49" fill={nut.color} fontSize="24" fontWeight="900" textAnchor="middle">{nut.value}</text>
                    </svg>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0a0a0a', marginTop: 1 }}>{nut.name}</div>
                    <div style={{ fontSize: 22, color: '#0a0a0a', fontWeight: 700 }}>{nut.unit}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pairing suggestion */}
          <div style={{ marginTop: 16 }}>
            <div style={{
              padding: '14px 18px',
              background: 'rgb(237, 194, 118)',
              borderRadius: 12,
              border: '2px solid rgb(240, 176, 65)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 30 }}>🍵</span>
                <span style={{ fontSize: 24, color: '#92400e', fontWeight: 800 }}>搭配推荐</span>
              </div>
              <div style={{ fontSize: 22, color: '#92400e', lineHeight: 1.5, fontWeight: 700 }}>{p.pairingSuggestion}</div>
            </div>
          </div>
        </div>

        {/* ===== Right column: steps + timer + tips ===== */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14, marginTop: -20 }}>
          {/* Steps header with timer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 26, color: '#92400e', letterSpacing: '0.1em', fontWeight: 700 }}>👨‍🍳 烹饪步骤</div>
            {/* Mini timer */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="56" height="56" viewBox="0 0 56 56">
                <circle cx="28" cy="28" r="23" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="4" />
                <circle
                  cx="28" cy="28" r="23" fill="none" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 23}
                  strokeDashoffset={2 * Math.PI * 23 * (1 - timerAngle / 360)}
                  transform="rotate(-90, 28, 28)"
                />
                <text x="28" y="33" fill="#92400e" fontSize="18" fontWeight="800" textAnchor="middle">
                  {activeStep + 1}/{p.steps.length}
                </text>
              </svg>
              <div style={{ fontSize: 22, color: '#92400e', fontWeight: 600 }}>
                步骤 {activeStep + 1} 进行中
              </div>
            </div>
          </div>

          {/* Steps list */}
          <div style={{ flex: 1 }}>
            {p.steps.map((step, i) => {
              const start = stepStart + i * stepInterval;
              const opacity = interpolate(frame, [start, start + 18], [0, 1], { extrapolateRight: 'clamp' });
              const slideX = interpolate(frame, [start, start + 18], [20, 0], { extrapolateRight: 'clamp' });
              const isActive = i === activeStep;
              const isDone = i < activeStep;

              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 12,
                    marginBottom: 10,
                    opacity,
                    transform: `translateX(${slideX}px)`,
                    padding: '8px 12px',
                    background: isActive ? 'rgba(245,158,11,0.08)' : 'transparent',
                    borderRadius: 12,
                    border: isActive ? '1px solid rgba(245,158,11,0.2)' : '1px solid transparent',
                  }}
                >
                  {/* Step number badge */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: isDone ? '#22c55e' : isActive ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(0,0,0,0.04)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: isDone ? 16 : 20,
                        fontWeight: 800,
                        color: isDone || isActive ? 'white' : '#92400e',
                        boxShadow: isActive ? '0 2px 8px rgba(245,158,11,0.3)' : 'none',
                      }}
                    >
                      {isDone ? '✓' : step.icon}
                    </div>
                    {/* Connector line */}
                    {i < p.steps.length - 1 && (
                      <div style={{
                        width: 2,
                        height: 10,
                        background: isDone ? '#22c55e' : 'rgba(0,0,0,0.06)',
                        borderRadius: 1,
                      }} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 24, fontWeight: 600, color: isActive ? '#92400e' : '#1c1917', lineHeight: 1.4 }}>{step.text}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <div style={{ fontSize: 24, color: '#92400e', fontWeight: 600 }}>⏱ {step.duration}</div>
                      {isActive && (
                        <div style={{
                          padding: '2px 10px',
                          background: '#f59e0b',
                          borderRadius: 8,
                          fontSize: 20,
                          color: 'white',
                          fontWeight: 700,
                          letterSpacing: '0.05em',
                        }}>
                          当前
                        </div>
                      )}
                      {isDone && (
                        <div style={{
                          padding: '2px 10px',
                          background: '#22c55e',
                          borderRadius: 8,
                          fontSize: 20,
                          color: 'white',
                          fontWeight: 700,
                        }}>
                          完成
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div style={{ transform: 'translateY(-20px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 24, color: '#1a1a1a', fontWeight: 700 }}>烹饪进度</span>
              <span style={{ fontSize: 24, color: '#1a1a1a', fontWeight: 800 }}>{Math.round(((activeStep + 1) / p.steps.length) * 100)}%</span>
            </div>
            <div style={{ height: 12, background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${((activeStep + 1) / p.steps.length) * 100}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #f59e0b, #22c55e)',
                  borderRadius: 4,
                }}
              />
            </div>
          </div>

          {/* Storage info */}
          <div style={{ transform: 'translateY(-15px)' }}>
            <div style={{
              padding: '14px 18px',
              background: 'rgba(34,197,94,0.55)',
              borderRadius: 10,
              border: '2px solid rgba(34,197,94,0.6)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}>
              <span style={{ fontSize: 34 }}>🧊</span>
              <div>
                <div style={{ fontSize: 22, color: '#92400e', fontWeight: 800 }}>保存方式</div>
                <div style={{ fontSize: 22, color: '#92400e', fontWeight: 700, lineHeight: 1.4 }}>{p.storageInfo}</div>
              </div>
            </div>
          </div>

          {/* Chef tip */}
          <div style={{ transform: 'translateY(-10px)' }}>
            <div
              style={{
                padding: '16px 20px',
                background: 'linear-gradient(135deg,rgb(208, 147, 115),rgb(216, 174, 149))',
                borderRadius: 14,
                boxShadow: '0 4px 24px rgba(69,26,3,0.5)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 22, color: '#fbbf24', fontWeight: 800, textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>👨‍🍳 厨师小贴士</div>
                <div style={{ display: 'flex', gap: 3 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} style={{ fontSize: 18, color: star <= Math.round(p.rating) ? '#fbbf24' : 'rgba(255,255,255,0.25)' }}>★</span>
                  ))}
                </div>
              </div>
              <div style={{ fontSize: 24, color: '#ffffff', lineHeight: 1.5, fontWeight: 700, textShadow: '0 1px 6px rgba(0,0,0,0.45)' }}>{p.chefTip}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ====== Decorative corner elements ====== */}
      {[
        { top: 0, left: 0, deg: 0 },
        { top: 0, right: 0, deg: 90 },
        { bottom: 0, right: 0, deg: 180 },
        { bottom: 0, left: 0, deg: 270 },
      ].map((pos, i) => {
        const { deg, ...cssPos } = pos;
        const cornerOpacity = interpolate(frame, [i * 8, i * 8 + 20], [0, 0.35], { extrapolateRight: 'clamp' });
        return (
          <svg
            key={`corner-${i}`}
            width="100"
            height="100"
            viewBox="0 0 100 100"
            style={{
              position: 'absolute',
              ...cssPos,
              opacity: cornerOpacity,
              transform: `rotate(${deg}deg)`,
              pointerEvents: 'none' as const,
            }}
          >
            <path d="M 0 40 Q 0 0 40 0" fill="none" stroke="#d97706" strokeWidth="3" />
            <path d="M 0 65 Q 0 0 65 0" fill="none" stroke="#d97706" strokeWidth="2.5" />
            <circle cx="8" cy="8" r="4" fill="#f59e0b" />
          </svg>
        );
      })}

      {/* ====== Bottom progress ====== */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: 4,
          background: 'rgba(0,0,0,0.06)',
        }}
      >
        <div
          style={{
            width: `${interpolate(frame, [0, durationInFrames - 1], [0, 100], { extrapolateRight: 'clamp' })}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #f59e0b, #ef4444)',
          }}
        />
      </div>
    </AbsoluteFill>
  );
};
